import type { Range } from "@tiptap/react";
import type { CSSProperties } from "react";

import type {
  RelatedAINote,
  RelatedCompany,
  RelatedDeal,
  RelatedDocument,
  RelatedFolder,
  RelatedFund,
  RelatedItem,
  RelatedMeeting,
  RelatedMember,
  RelatedOpportunity,
  RelatedPerson,
  RelatedThemes,
  RelatedThread,
} from "@app-types/related-items.type";
import { FundStatus } from "@app-types/common.type";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    mention: {
      /**
       * Set a mention at the current position
       *
       * @param props - Mention node attributes or related item
       */
      setMention: (props: MentionNodeAttrs | RelatedItem) => ReturnType;
      /**
       * Set a mention at the specified position
       *
       * @param at - Position to set the mention at (number or range)
       * @param props - Mention node attributes or related item
       */
      setMentionAt: (at: number | Range, props: MentionNodeAttrs | RelatedItem) => ReturnType;
    };
  }

  interface EditorEvents {
    /**
     * Event that is emitted when a mention is added
     */
    mentionAdded: {
      item: RelatedItem | MentionNodeAttrs;
      attrs: MentionNodeAttrs;
    };
  }
}

export interface MentionOptions {
  HTMLAttributes: Record<string, any>;
  onClick: (item: MentionNodeAttrs) => void;
  verticalAlign: CSSProperties["verticalAlign"];
}

export type MentionNodeAttrs =
  | {
      type: "deal";
      id: string;
      title: string;
      stage: string;
      stage_color: string;
      __original_item: RelatedDeal;
      object_type: "deals.deal";
    }
  | {
      type: "meeting";
      id: string;
      title: string;
      start_time: string;
      color: string;
      __original_item: RelatedMeeting;
      object_type: "meetings.meeting";
    }
  | {
      type: "company";
      id: string;
      title: string;
      logo: string;
      __original_item: RelatedCompany;
      object_type: "companies.company";
    }
  | {
      type: "person";
      id: string;
      first_name: string;
      last_name: string;
      email: string;
      title: string;
      logo: string;
      __original_item: RelatedPerson;
      object_type: "people.person";
    }
  | {
      type: "document";
      id: string;
      title: string;
      deal?: string;
      is_memo: boolean;
      file: string;
      __original_item: RelatedDocument;
      object_type: "documents.document";
    }
  | {
      type: "folder";
      id: string;
      title: string;
      __original_item: RelatedFolder;
      object_type: "documents.folder";
    }
  | {
      type: "theme";
      id: string;
      title: string;
      logo: string;
      __original_item: RelatedThemes;
      object_type: "sourcing.theme";
    }
  | {
      type: "member";
      id: string;
      first_name: string;
      last_name: string;
      email: string;
      logo: string;
      title: string;
      __original_item: RelatedMember;
      object_type: "accounts.user";
    }
  | {
      type: "note";
      id: string;
      title: string;
      access: string;
      description: string;
      state: string;
      created: string;
      modified: string;
      object_type: "notes.ainote";
      __original_item: RelatedAINote;
    }
  | {
      type: "thread";
      id: string;
      title: string;
      object_type: "emails.thread";
      logo: string;
      __original_item: RelatedThread;
    }
  | {
      type: "fund";
      id: string;
      title: string;
      status: FundStatus;
      object_type: "fundraising.fund";
      __original_item: RelatedFund;
    }
  | {
      type: "opportunity";
      id: string;
      title: string;
      stage: string;
      stage_color: string;
      object_type: "fundraising.opportunity";
      __original_item: RelatedOpportunity;
    };
